<template>
  <div>
    <div
      v-for="feature in features"
      :key="feature.feature"
      class="feature-row d-flex align-center flex-wrap ga-3 px-4 py-2"
      :class="{
        'recently-changed': recentKey === `feature:${feature.feature}`,
        'feature-row--inactive': !moduleActive,
      }"
    >
      <!-- Menu-initiated mutations (suspend/revoke/trial) have no button of
           their own to carry a spinner. -->
      <v-progress-linear
        v-if="pendingKey === `feature:${feature.feature}`"
        class="feature-row-progress"
        indeterminate
        color="primary"
      />
      <div class="feature-main d-flex align-center ga-3 flex-grow-1">
        <!-- Fixed-width tier gutter so descriptions line up whether or not the
             row carries a chip. Incluído (locked_on) stays neutral; Add-on
             takes the primary tint, matching the module chip. Togglable
             defaults carry no chip — see featureAccessChip. -->
        <div class="feature-access flex-shrink-0">
          <v-chip
            v-if="featureAccessChip(feature)"
            size="small"
            variant="text"
            :class="[
              'px-1',
              featureAccessChip(feature).color ? '' : 'text-medium-emphasis',
            ]"
            :color="featureAccessChip(feature).color"
            :prepend-icon="featureAccessChip(feature).icon"
          >
            {{ featureAccessChip(feature).label }}
          </v-chip>
        </div>
        <!-- Description + any meta on one line so the row keeps a single,
             uniform height. -->
        <div class="d-flex align-center flex-wrap ga-2">
          <span class="text-body-2">{{ feature.description }}</span>
          <span
            v-if="metaLine(feature)"
            class="text-caption text-medium-emphasis"
          >
            {{ metaLine(feature) }}
          </span>
        </div>
      </div>

      <div class="d-flex align-center ga-3 flex-shrink-0">
        <!-- Trial expiry sits inline before the chip (not stacked under it) so
             trial rows stay the same height as the rest. -->
        <span
          v-if="feature.trial_expires_at"
          class="text-caption text-medium-emphasis"
        >
          Trial expira {{ formatFromNow(feature.trial_expires_at) }}
        </span>
        <v-chip :color="featureStatusDisplay(feature).color" size="small">
          {{ featureStatusDisplay(feature).label }}
        </v-chip>

        <!-- Fixed-width action gutter. The overflow menu only renders on some
             rows (granted add-ons); without a reserved slot, those rows push
             their status chip a button-width inward while menu-less rows (core /
             "Incluído") sit flush right — so the chips never line up. A
             right-justified, constant-width gutter anchors every chip at the
             same x, menu or no menu. Buttons (Conceder) are wider and pull their
             own row's chip in, but those are locked/grantable rows and read as
             distinct anyway. -->
        <div
          class="feature-actions d-flex align-center justify-end ga-1 flex-shrink-0"
        >
          <!-- An inactive module (locked/suspended/expired) renders its features
               as read-only context: no controls at all, not even disabled ones.
               The module must be granted from its own header first. -->
          <template v-if="canUpdate && moduleActive">
            <!-- core features: feature-level writes return 409; route the action
                 to the owning module. When the module is already granted there's
                 nothing to do here — the "Incluído" chip says it comes with the
                 module. -->
            <template v-if="feature.classification === 'core'">
              <v-btn
                v-if="feature.state === 'locked'"
                size="small"
                color="primary"
                variant="tonal"
                :loading="pendingKey === `module:${feature.module}`"
                :disabled="!!pendingKey"
                @click="$emit('grant-module', feature)"
              >
                Conceder módulo
              </v-btn>
            </template>
            <template v-else>
              <!-- Locked addon: "Conceder" grants it permanently; the overflow
                   menu offers a time-boxed trial as the alternative — same shape
                   as the module header's Conceder + trial. -->
              <v-btn
                v-if="feature.state === 'locked'"
                size="small"
                color="primary"
                variant="tonal"
                :loading="pendingKey === `feature:${feature.feature}`"
                :disabled="!!pendingKey"
                @click="$emit('grant', feature)"
              >
                Conceder
              </v-btn>
              <v-menu>
                <template v-slot:activator="{ props: menuProps }">
                  <!-- density=comfortable so the icon button is 28px (matching
                       the text buttons), not the 40px default that would make
                       this row taller than its siblings. -->
                  <v-btn
                    icon
                    size="small"
                    density="comfortable"
                    variant="text"
                    v-bind="menuProps"
                    :disabled="!!pendingKey"
                    aria-label="Mais ações"
                  >
                    <v-icon :icon="mdiDotsVertical" />
                  </v-btn>
                </template>
                <v-list density="compact">
                  <!-- The API rejects a trial on an already-usable grant, so the
                       option appears only when the feature is off — including the
                       locked state, where it's the alternative to "Conceder". -->
                  <v-list-item
                    v-if="feature.state !== 'entitled' && feature.state !== 'trialing'"
                    title="Iniciar trial"
                    @click="$emit('trial', feature)"
                  />
                  <!-- Suspend/resume/revoke act on an existing grant, so they
                       only apply once the feature has left the locked state. -->
                  <template v-if="feature.state !== 'locked'">
                    <v-list-item
                      v-if="feature.state === 'suspended'"
                      title="Reativar"
                      @click="$emit('resume', feature)"
                    />
                    <v-list-item
                      v-else
                      title="Suspender"
                      @click="$emit('suspend', feature)"
                    />
                    <v-list-item
                      title="Revogar"
                      base-color="error"
                      @click="$emit('revoke', feature)"
                    />
                  </template>
                </v-list>
              </v-menu>
            </template>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { mdiDotsVertical } from "@mdi/js";
import { useDateFormat } from "@/composables/useDateFormat";
import { featureAccessChip, featureStatusDisplay } from "./entitlementDisplay";

defineProps({
  features: { type: Array, required: true },
  pendingKey: { type: String, default: null },
  recentKey: { type: String, default: null },
  canUpdate: { type: Boolean, default: false },
  // The module must be granted (active or in trial) before its features can be
  // managed — addon grants 409 otherwise. When the owning module is in any
  // other state, the rows render muted with no controls (grant the module from
  // its header first). Defaults true for module-less features, which have no
  // such gate.
  moduleActive: { type: Boolean, default: true },
});

defineEmits(["grant-module", "grant", "trial", "suspend", "resume", "revoke"]);

const { formatFromNow } = useDateFormat();

// Headerless rows: every cell must describe itself, so visibility folds into a
// muted caption line under the description (classification has its own chip
// beside it; the status chip stays the single loud signal per row).
const metaLine = (feature) => {
  const parts = [];
  if (feature.visibility === "internal") {
    parts.push("Interno");
  }
  return parts.join(" · ");
};
</script>

<style scoped>
.feature-row {
  position: relative;
  /* Uniform single-line height so every feature row matches the quota rows,
     whether it carries a 28px button, an icon menu, or just a status chip. */
  min-height: 44px;
}

.feature-row + .feature-row {
  border-top: thin solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.feature-row-progress {
  position: absolute;
  top: 0;
  left: 0;
}

/* Keeps the description column readable; below this the status cluster wraps
   onto its own line. */
.feature-main {
  flex-basis: 260px;
  min-width: 260px;
}

/* Fixed tier-chip gutter: wide enough to hold the longest chip ("Incluído")
   so every description starts at the same x, chip or no chip. */
.feature-access {
  width: 104px;
}

/* Trailing action gutter mirrors the tier gutter on the left: a reserved slot —
   the width of the 28px comfortable icon button — so the status chip lands at
   the same x on every row, with or without the overflow menu. Right-justified,
   so the menu hugs the right edge; min-width lets the wider Conceder buttons
   spill leftward without breaking the no-menu/menu-only alignment. */
.feature-actions {
  min-width: 28px;
}

/* Below sm the fixed gutter wastes scarce width on a low-value tier marker;
   let it shrink to content and the row's natural reflow handle the rest. */
@media (max-width: 599.98px) {
  .feature-access {
    width: auto;
  }
}

/* Features under an inactive module are read-only context: dim the whole row
   so it reads as unavailable until the module itself is granted. */
.feature-row--inactive {
  opacity: 0.5;
}

.feature-row.recently-changed {
  animation: entitlement-row-flash 1.6s ease-out;
}

@keyframes entitlement-row-flash {
  from {
    background-color: rgba(var(--v-theme-primary), 0.12);
  }
  to {
    background-color: transparent;
  }
}
</style>
